import React, { useState } from 'react';
import styles from './PropertyMessage.scss';
import Stepper, { Step } from './Stepper/index';
import Error from './Error';
import PropertyMessageMessage from './PropertyMessageMessage';
import PropertyMessageContact from './PropertyMessageContact';
import PropertyRequest from '../utils/PropertyRequest';
import PropertyMessageDetails from './PropertyMessageDetails';
import { IModalOptions, alert } from './AsyncModal';
import PropertyMessageLogin from './PropertyMessageLogin';
import trackApi from '../utils/trackApi';
import { IUserStatus } from '../utils/UserRequest';
import useUser from '../stores/useUser';
import Owner from '../utils/Owner';
import { createToast } from './MaterialUi/Toast';
import ErrorIcon from '@mui/icons-material/Error';
import Affiliate, { IAffiliateDetails, IBankAffiliateList } from '../utils/Affiliate';
import IHome from '../interfaces/IHome';
import LogError from '../utils/LogError';
import Modal from './EmailDoi/Modal';
import Button from './MaterialUi/Button';
import JsonApiError from './JsonApiError';

export type IPropertyMessageProps = {
    id: string;
    needsPremium: boolean;
    marketingType: IHome['marketingType'];
    affiliatePartnerId?: string;
} & (
        {
            closeHandler: () => void;
            remoteClose?: IModalOptions['remoteClose'];
        } | {
            closeHandler?: () => void;
            remoteClose: IModalOptions['remoteClose'];
        }
    );

export interface IPropertyMessageState {
    userData: IUserStatus;
    message: string;
    isSubmitting: boolean;
}


const PropertyMessage: React.FC<React.PropsWithChildren<IPropertyMessageProps>> = ({ id = '', closeHandler = () => { }, remoteClose, needsPremium = false, marketingType, affiliatePartnerId }) => {
    // const [userData, setUserData] = React.useState<IUserStatus>({});
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const [user, userActions] = useUser();
    const [userData, setUserData] = useState(user);
    const [message, setMessage] = React.useState<string>(user.lastMessage || '');
    const [hasDocuments, setHasDocuments] = useState<boolean>(false);
    const [showPremiumTeaser, setShowPremiumTeaser] = useState<boolean>(false);
    const [affiliateBank, setAffiliateBank] = useState<IAffiliateDetails>({} as IAffiliateDetails);
    const [wantFinancingOffer, setWantFinancingOffer] = useState<boolean>(false);
    const [isOpen, setIsOpen] = React.useState(false);
    const [isMailVerified, setIsMailVerified] = React.useState(false);
    const [view, setView] = useState<'form' | 'financingDetails'>('form');
    const [wantFinancialAdvisor, setWantFinancialAdvisor] = useState<boolean>(false);

    React.useEffect(() => {
        updateUserStatus();
    }, []);

    React.useEffect(() => {
        setUserData(user);
        if (Affiliate.isBankAffiliate(user.affiliateName)) {
            Affiliate.getDetails(user.affiliateName).then((affiliate) => {
                setAffiliateBank(affiliate);
            })
        }

        if (user.isAdmin) {
            Owner(user.id)
                .getDocuments()
                .then((files) => {
                    if (files.length) {
                        setHasDocuments(true);
                    } else {
                        setHasDocuments(false);
                    }
                })
                .catch(console.error);
        }
    }, [user]);

    const updateUserStatus = () => {
        userActions.getStatus().catch((e) => {
            console.error(e);
        });
    };

    const handleOpen = () => {
        setIsOpen(true);
    };

    const handleModalClose = () => {
        setIsOpen(false);
    };

    const handleSubmit = () => {
        setIsMailVerified(true);
        handleModalClose();
    };

    const onSubmitMessage = (form?: HTMLFormElement) => {
        if (form && form.elements) {
            for (let i = 0; i < form.elements.length; i++) {
                const elm = form.elements[i] as HTMLInputElement;
                if (elm.name === 'message') {
                    setMessage(elm.value);
                }
            }
            // adformTrackApi.click('propertyMessageButton');
        }
    };

    const onSubmitContact = (form?: HTMLFormElement) => {
        if (isMailVerified || user.mailVerified) {
            if (!needsPremium || user.isPremium) {
                if (wantFinancialAdvisor) {
                    setView('financingDetails');
                } else {
                    submitPropertyRequest();
                    setView('form');
                }
            } else {
                setShowPremiumTeaser(true);
            }
        } else {
            handleOpen();
        }
    };

    // const submitAll = (data: IPropertyRequestAddMessageSubmit) => {
    const submitPropertyRequest = () => {
        setIsSubmitting(true);

        const data = getDataFromState();

        userActions.setStatus(data.userData);
        // adformTrackApi.click('propertyMessageContact');

        return PropertyRequest.addMessage(data)
            .then((res) => {
                trackApi
                    .wglTarget('ANFRAGE', {typeofmarketing: marketingType})
                    // trackApi
                    //     .event('Makler', 'Nachricht', 'Buttonclick')
                    .then(() => {
                        createToast('Die Nachricht wurde gesendet', 'success');
                        const data = res.getData();
                        if (data.redirect_url) {
                            location.assign(data.redirect_url);
                        }
                    })
                    .catch((e) => {
                        Promise.reject();
                        setIsSubmitting(false);
                        console.error('ga error', e);
                    });
            })
            .catch((e) => {
                console.error(e);
                setIsSubmitting(false);
                userActions.refreshStatus();
                // Promise.reject();
                if (e?.responseData?.message) {
                    alert(
                        <>
                            <Error>Fehler beim Speichern der Nachricht!</Error>
                            <p>{e.responseData.message}</p>
                        </>
                    );
                    LogError.add(e, {
                        title: 'Fehler beim Speichern der Nachricht',
                        error_code: 'PropertyMessage.tsx:submitPropertyRequest:errorMessage',
                        request_data: { data }
                    });
                } else {
                    createToast(<JsonApiError error={e} text="Fehler beim Speichern der Nachricht!" />, 'error');
                    LogError.add(e, {
                        title: 'Fehler beim Speichern der Nachricht',
                        error_code: 'PropertyMessage.tsx:submitPropertyRequest:errorCode',
                        request_data: { data }
                    });
                }
            });

    };

    const getDataFromState = () => {
        const whitelistKeys: Array<keyof IUserStatus> = [
            'salutation',
            'given_name',
            'family_name',
            'address_line1',
            'postal_code',
            'locality',
            'mail',
            'phone',
        ];
        const nextUserData = Object.assign({}, ...whitelistKeys.map((key) => ({ [key]: userData[key] })));
        const data = { userData: nextUserData, message: message, id: id, financing_offer: wantFinancingOffer };
        return data;
    };

    const submitAddPremium = () => {
        setIsSubmitting(true);
        const data = getDataFromState();
        userActions.setStatus(data.userData);

        return PropertyRequest.addMessageWithPremium(data)
            .then((res) => {
                const redirectData = res.getData();
                if (redirectData.redirect_url) {
                    location.assign(redirectData.redirect_url);
                } else {
                    console.error('redirectData.url is missing');
                }
            })
            .catch((e) => {
                console.error(e);
                setIsSubmitting(false);
                Promise.reject();
                createToast(`Fehler ${e.statusCode} beim Speichern`, 'error');
                LogError.add(e, {
                    title: 'Fehler beim Speichern der Anfrage mit Premium',
                    error_code: 'PropertyMessage.tsx:submitAddPremium',
                    request_data: { data }
                });
            });
    };

    const handleChangeContact = (ev: React.FormEvent) => {
        const elm = ev.currentTarget as HTMLInputElement;
        setUserData({ ...userData, [elm.name]: elm.value });
    };

    // const saveContact = (contactData) => {
    //     userActions.setStatus({ ...user, ...contactData });
    // };

    const onLogin = () => {
        updateUserStatus();
        // adformTrackApi.click('propertyMessageLogin');
        //set affiliate when not set before or set in PropertySetAffiliateStepperSelect and when id ist not empty
        if (affiliatePartnerId && !user.affiliateName) {
            Affiliate.getAllBankAffiliates().then((list) => {
                const name = list.find((a) => a.uuid === affiliatePartnerId)?.affiliate_name || '';
                Affiliate.saveAffiliate({ name }, user.uid).catch((e) => {
                    console.error(e);
                });
            });
        }

    };

    const onRegister = () => {
        updateUserStatus();
        // adformTrackApi.click('propertyMessageRegister');
        //set affiliate when not set before or set in PropertySetAffiliateStepperSelect and when id ist not empty
        if (affiliatePartnerId && !user.affiliateName) {
            Affiliate.getAllBankAffiliates().then((list) => {
                const name = list.find((a) => a.uuid === affiliatePartnerId)?.affiliate_name || '';
                Affiliate.saveAffiliate({ name }, user.uid).catch((e) => {
                    console.error(e);
                });
            });
        }
    };

    const handleClose = () => {
        closeHandler();
        if (remoteClose && remoteClose.close) {
            remoteClose.close();
        }
    };

    const isUserLoggedIn = user.isLoggedIn;
    const name = user.given_name && user.family_name ? `${user.given_name} ${user.family_name}` : user.mail;

    const trackUrl = window.location.pathname + '?modal=addPropertyMessage';

    const hasBankAffiliate = Affiliate.isBankAffiliate(user.affiliateName) && affiliateBank?.display_name;

    // const affiliateBank = getAffiliateBank();

    return (
        <div className={styles.propertyMessage}>
            {view === 'form' ? (<Stepper>
                <Step title="Deine Nachricht" nextHandler={onSubmitMessage} trackPageviewUrl={`${trackUrl}&step=message`}>
                    <PropertyMessageMessage defaultMessage={message} />
                </Step>
                <Step
                    title="Anmelden"
                    withoutForm={!isUserLoggedIn}
                    disabled={!message}
                    trackPageviewUrl={`${trackUrl}&step=${isUserLoggedIn ? 'loggedin' : 'login'}`}
                >
                    {!isUserLoggedIn ? <PropertyMessageLogin loginHandler={onLogin} registerHandler={onRegister} /> : <p>Angemeldet als {name}</p>}
                </Step>
                <Step
                    title="Deine Kontaktdaten"
                    tooltip={
                        <>
                            <h1>Warum Kontaktdaten angeben?</h1>
                            Unsere Makler möchten sich so gut es geht ein Bild von Dir machen. Deswegen wünschen sie Deine vollständigen Kontaktangaben, um Dir
                            ein Exposé des Objekts sowie weitere Informationen oder Besichtigungstermine zu schicken. Ohne die Angaben dieser Daten ist eine
                            Anfrage leider nicht möglich. Übrigens: Deine Informationen sind bei uns sicher - wir verwenden sie nicht ohne Deine Zustimmung.
                            Also einfach Formular ausfüllen, absenden und mit etwas Glück Exposé, Besichtigungstermin etc. erhalten.
                        </>
                    }
                    nextHandler={onSubmitContact}
                    buttonText={'Absenden'}
                    buttonDisclaimer={
                        marketingType === 'buy' ?
                            hasBankAffiliate ? <>
                                Bitte nimm die <a href="/info/datenschutz" target="_blank">Datenschutzhinweise</a> von Wohnglück sowie
                                die <a href={affiliateBank.privacy_uri || affiliateBank.pia_prod} target="_blank">Datenschutzhinweise</a>
                                {' '}der {affiliateBank.display_name} zur Kenntnis.
                            </> : <>Bitte nimm die <a href="/info/datenschutz" target="_blank">Datenschutzhinweise</a> von Wohnglück zur Kenntnis.</>
                            : null}
                    disabled={!message || !isUserLoggedIn || showPremiumTeaser}
                    isSubmitting={isSubmitting}
                    withoutForm={showPremiumTeaser}
                    trackPageviewUrl={`${trackUrl}&step=${showPremiumTeaser ? 'premium' : 'personal_data'}`}
                >
                    {!showPremiumTeaser ? (
                        <PropertyMessageContact
                            data={userData}
                            onChange={handleChangeContact}
                            hasDocuments={hasDocuments}
                            setWantFinancingOffer={setWantFinancingOffer}
                            wantFinancingOffer={wantFinancingOffer}
                            marketingType={marketingType}
                            propertyId={id}
                            setWantFinancialAdvisor={setWantFinancialAdvisor}
                            wantFinancialAdvisor={wantFinancialAdvisor}
                        />
                    ) : (
                        <div className={styles.premium}>
                            <h1>
                                <ErrorIcon /> Werde Premium-Kunde, um anzufragen!
                            </h1>
                            <p>
                                Der Anbieter hat diese Immobilie »exklusiv« bei uns inseriert - sie kann nur von Premium-Kunden angefragt werden. Unser Team
                                arbeitet permanent daran, dass wir die besten Deals der Stadt bieten können - diesen Service gibt’s aber nur für unsere
                                Premium-Kunden.
                            </p>
                            <div className={styles.actions}>
                                <Button onClick={submitAddPremium}>Premium-Kunde werden und anfragen</Button>

                                <Button variant="text" color="inherit" onClick={handleClose}>
                                    Anfrage abbrechen
                                </Button>
                            </div>
                            <p className={styles.annotation}>
                                Monatlich 19,99&nbsp;€, jederzeit kündbar.
                            </p>
                        </div>
                    )}
                </Step> 
            </Stepper>) : (view === 'financingDetails' && wantFinancialAdvisor === true) ? (
                <div>
                    <h3>Vervollständige deine Daten</h3>
                    <div>
                        <p className={styles.description}>Du hast hier die Möglichkeit, optional Daten zu deiner Haushaltssituation zu erfassen. 
                            Die Daten werden im Rahmen deines Wunsches zur Finanzierungsberatung mit den relevanten Wohnglück-Berater:innen geteilt. 
                            Dies ist sinnvoll, damit die potentiellen Berater:innen dich besser unterstützen können.</p>
                    </div>
                    <PropertyMessageDetails 
                        submitPropertyRequest={submitPropertyRequest}
                    />
                </div>)
                : null}
            <Modal
                open={isOpen}
                onClose={handleModalClose}
                userMail={user.mail}
                onSubmitAction={handleSubmit}
                submitButtonText={"Weiter"}
                successText={"Du kannst deine Anfrage nun absenden."}
                formElement={"div"}
            >
            </Modal>
        </div>
    );
};

export default PropertyMessage;
